import { VideoSegmentManager } from "../VideoSegment/VideoSegmentManager";
import { PreCacheItem, PrecacheItemType, SinglePreCacheItem } from "./pre-cache-item";

/**
 * Handles pre-caching for the app
 */
export class PreCacheManager {
    /**
     * Holds all items to precache, this will get filled as the game registers items
     */
    public items: PreCacheItem[] = [];

    /**
     * Used for progress tracking. All the currently loading assets
     */
    private loadCount = 0;

    /**
     * The parent of all the loaded assets
     */
    private loadItemsParent: any = undefined;

    /**
     * Will be internally set to true if we are in the process of cancelling
     */
    private isCancelling = false;

    /**
     * Registers an item for pre-caching. This needs to be called for every class that derives from PreCachItem
     * @param item The item to precache
     */
    public registerItem(item: PreCacheItem) {
        this.items.push(item);
    }

    public registerVideoSegmentManager(videoSegmentManager: VideoSegmentManager) {
        const context = this;
        videoSegmentManager.items.map((itemInstance, indx) => {
            context.items.push(itemInstance);
        })
    }

    /**
     * Pre-caches all items that have been registered
     * @param onUpdate Called every time there is a progress update. (Every time a single asset has been cached)
     * @param onDone Called once the pre-caching process has been completed
     */
    public loadAllItems(onUpdate: (percentage: number) => void, onDone: () => void) {
        this.loadCount = 0;
        this.isCancelling = false;
		this.loadItemsParent = document.createElement('div');
        this.loadItemsParent.style.display = 'none';
        document.body.appendChild(this.loadItemsParent);
        var flatItems: SinglePreCacheItem[] = [];
        
        for(var indx = 0; indx < this.items.length; indx++) {
            flatItems = flatItems.concat(this.items[indx].getItems());
        }

        const onSingleItemDone = () => {
            this.loadCount++;

            const percentage = this.loadCount / flatItems.length;
            onUpdate(percentage);

            if(percentage === 1) {
                this.loadItemsParent.remove();
                this.loadItemsParent = undefined;
                onDone();
            }
        }

		for (var indx = 0; indx < flatItems.length; indx++) {
			this.preloadAsset(flatItems[indx], onSingleItemDone);
		}
    }

    /**
     * Cancells the pre-cache process
     */
    public cancelLoadAllItems () {
        this.isCancelling = true;
        if(this.loadItemsParent) {
            this.loadItemsParent.remove();
            this.loadItemsParent = undefined;
        }
    }

    /**
     * Pre-caches a single asset
     * @param item The item to pre-cache
     * @param onDone  Called once this specific item/asset has been pre-cached
     */
    private preloadAsset = (item: SinglePreCacheItem, onDone: () => void) => {
		var asset: any = null;

        if (item.type === PrecacheItemType.Image) {
            asset = document.createElement('img');
        } else if (item.type === PrecacheItemType.Video) {
            asset = document.createElement('video');
        } else {
            asset = document.createElement('audio');
        }

        const onLoad = () => {
            if(this.isCancelling) {
                return;
            }
            asset.remove();

            onDone();
        }
        if(item.type === PrecacheItemType.Image) {
            asset.onload = () => {
                onLoad();
            }
        } else {
            const check = () => {
                if((asset as HTMLVideoElement).readyState === 4) {
                    onLoad();
                } else {
                    setTimeout(() => {
                        check();
                    }, 100);
                }
            }

            setTimeout(() => {
                check();
            }, 100);
        }

        asset.src = item.src;

        this.loadItemsParent.appendChild(asset);
    }
}